<template>
  <div
    v-if="current"
    role="status"
    aria-live="polite"
    class="platform-notice-banner"
    :class="`platform-notice-banner--${current.tone}`"
  >
    <i :class="ICON[current.tone]" aria-hidden="true" />
    <p class="platform-notice-banner__text">
      <strong>{{ current.label }}:</strong>
      {{ current.title }}
      <span v-if="current.body" class="platform-notice-banner__detail">
        · {{ current.body }}
      </span>
    </p>
    <div v-if="items.length > 1" class="platform-notice-banner__pager">
      <button type="button" aria-label="Previous notice" @click="step(-1)">
        <i class="el-icon-arrow-left" />
      </button>
      <span>{{ index + 1 }} of {{ items.length }}</span>
      <button type="button" aria-label="Next notice" @click="step(1)">
        <i class="el-icon-arrow-right" />
      </button>
    </div>
    <a
      v-if="current.linkUrl"
      :href="current.linkUrl"
      target="_blank"
      rel="noopener"
      class="platform-notice-banner__link"
    >
      {{ current.linkLabel || "Learn more" }}
    </a>
    <button
      v-if="current.dismissible"
      type="button"
      aria-label="Dismiss"
      @click="dismiss(current)"
    >
      <i class="el-icon-close" />
    </button>
  </div>
</template>

<script>
import moment from "moment";
import {
  getLivePlatformNotices,
  getStatusIncidents,
} from "@/api/platform-notices/methods.js";

const APP_KEY = "omnia";
const POLL_MS = 60000;
const DISMISS_KEY = "platform-notices-dismissed";
const LABEL = {
  announcement: "Announcement",
  news: "News",
  incident: "Incident",
  maintenance: "Maintenance",
};
const RANK = { critical: 0, warning: 1, maintenance: 2, info: 3 };

const incidentTone = (i) =>
  i.impact === "maintenance" || i.scheduled_for
    ? "maintenance"
    : i.impact === "major" || i.impact === "critical"
    ? "critical"
    : "warning";

const incidentLabel = (i, tone) => {
  if (tone === "maintenance") {
    return i.status === "scheduled" ? "Scheduled maintenance" : "Maintenance";
  }
  if (i.impact === "critical") return "Major outage";
  if (i.impact === "major") return "Partial outage";
  return "Degraded performance";
};

const fromIncident = (i) => {
  const tone = incidentTone(i);
  return {
    key: `${i.id}:${i.updated_at}`,
    tone,
    label: incidentLabel(i, tone),
    title: i.name,
    body:
      i.status === "scheduled" && i.scheduled_for
        ? `Starts ${moment(i.scheduled_for).fromNow()}`
        : i.status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
    linkUrl: i.shortlink || "https://status.voxo.co/",
    linkLabel: "View status",
    dismissible: 1,
  };
};

const readDismissed = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]");
  } catch (e) {
    return [];
  }
};

export default {
  name: "PlatformNoticeBanner",
  data() {
    return {
      notices: [],
      incidents: [],
      dismissed: readDismissed(),
      index: 0,
      lastFetch: 0,
      inflight: false,
      timer: null,
      ICON: {
        critical: "el-icon-warning",
        warning: "el-icon-warning",
        maintenance: "el-icon-setting",
        info: "el-icon-info",
      },
    };
  },
  computed: {
    items() {
      const hidden = new Set(this.dismissed);
      // Dismissal is per notice *version*: an edit/update shows it again.
      const notices = this.notices.map((n) => ({
        ...n,
        key: `pn:${n.id}:${n.updatedAt}`,
        tone: n.type === "maintenance" ? "maintenance" : n.severity,
        label: LABEL[n.type] || "Notice",
      }));
      return [...this.incidents.map(fromIncident), ...notices]
        .filter((n) => !hidden.has(n.key))
        .sort((a, b) => RANK[a.tone] - RANK[b.tone]);
    },
    current() {
      return this.items[this.index] || null;
    },
  },
  watch: {
    "items.length"(n) {
      if (this.index >= n) this.index = Math.max(0, n - 1);
    },
  },
  created() {
    this.load();
    // Forced: lastFetch is stamped when a fetch finishes, so a throttled tick
    // lands a few hundred ms short of POLL_MS and every other tick is skipped.
    this.timer = setInterval(() => {
      if (document.visibilityState === "visible") this.load(true);
    }, POLL_MS);
    document.addEventListener("visibilitychange", this.onVisible);
    window.addEventListener("focus", this.onVisible);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onVisible);
    window.removeEventListener("focus", this.onVisible);
  },
  methods: {
    onVisible() {
      if (document.visibilityState === "visible") this.load();
    },
    async load(force = false) {
      if (this.inflight) return;
      if (!force && Date.now() - this.lastFetch < POLL_MS) return;
      this.inflight = true;
      const [notices, incidents] = await Promise.all([
        getLivePlatformNotices(APP_KEY),
        getStatusIncidents(),
      ]);
      if (notices) this.notices = notices;
      if (incidents) this.incidents = incidents;
      this.lastFetch = Date.now();
      this.inflight = false;
    },
    step(d) {
      const n = this.items.length;
      this.index = (this.index + d + n) % n;
    },
    dismiss(item) {
      this.dismissed = [...this.dismissed, item.key].slice(-50);
      try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify(this.dismissed));
      } catch (e) {
        /* storage unavailable: dismiss for this session only */
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/scss/_variables";

.platform-notice-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 8px 16px;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  flex-shrink: 0;

  &--critical {
    background: $text-color-danger;
    color: #fff;
  }
  &--warning {
    background: $text-color-warning;
    color: $text-color;
  }
  &--maintenance {
    background: $text-color;
    color: #fff;
  }
  &--info {
    background: $text-color-accent;
    color: #fff;
  }

  button {
    display: inline-flex;
    padding: 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__detail {
    opacity: 0.85;
  }

  &__pager {
    display: flex;
    align-items: center;
    gap: 4px;
    font-variant-numeric: tabular-nums;
  }

  &__link {
    color: inherit;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
